import React,{useState} from 'react';

function formatCurrency(cents) {
  return new Intl.NumberFormat('en-AU',{
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(cents || 0) / 100);
}

function formatCreatedDate(value) {
  return new Intl.DateTimeFormat('en-AU',{
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(new Date(value));
}

function QuoteTable({
  quotes,
  view,
  onEdit,
  onDelete,
  onArchive,
  onRestore
}) {
  const [quoteToDelete,setQuoteToDelete]=useState(null);
  const [quoteToArchive,setQuoteToArchive]=useState(null);
  const [actionLoading,setActionLoading]=useState(false);
  const [actionError,setActionError]=useState('');

  const openDialog=(quote,type)=> {
    setActionError('');
    if (type==='delete') {
      setQuoteToDelete(quote);
    } else {
      setQuoteToArchive(quote);
    }
  };

  const closeDialogs=()=> {
    if (!actionLoading) {
      setQuoteToDelete(null);
      setQuoteToArchive(null);
      setActionError('');
    }
  };

  const confirmAction=async ()=> {
    if (actionLoading) {
      return;
    }

    const quote=quoteToDelete || quoteToArchive;
    if (!quote) {
      return;
    }

    setActionLoading(true);
    setActionError('');

    try {
      if (quoteToDelete) {
        await onDelete(quote);
      } else {
        await onArchive(quote);
      }

      setQuoteToDelete(null);
      setQuoteToArchive(null);
    } catch (error) {
      console.error('Quote action failed:',error);
      setActionError(error.message || 'Unable to complete this action.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore=async (quote)=> {
    if (actionLoading) {
      return;
    }

    setActionLoading(true);
    setActionError('');

    try {
      await onRestore(quote);
    } catch (error) {
      console.error('Quote restore failed:',error);
      setActionError(error.message || 'Unable to restore quotation.');
    } finally {
      setActionLoading(false);
    }
  };

  const dialogQuote=quoteToDelete || quoteToArchive;
  const dialogType=quoteToDelete ? 'delete' : 'archive';

  return (
    <>
      <div className="table-shell">
        {quotes.length===0 ? (
          <p style={{padding: '24px 16px',color: 'var(--muted)',fontSize: '13px'}}>
            No quotations found
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Client / Company</th>
                <th>Project</th>
                <th>Base Value</th>
                <th>Status</th>
                <th>Created</th>
                <th><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((quote)=> (
                <tr key={quote.id}>
                  <td className="reference">{quote.quote_reference}</td>
                  <td>{quote.client_company}</td>
                  <td className="project-cell">
                    {quote.quote_title?.trim() || quote.project_name}
                  </td>
                  <td>{formatCurrency(quote.base_total_cents)}</td>
                  <td>
                    <span className={`status status--${quote.status}`}>
                      {quote.status}
                    </span>
                  </td>
                  <td>{formatCreatedDate(quote.created_at)}</td>
                  <td>
                    <span style={{display: 'inline-flex',gap: '12px',alignItems: 'center'}}>
                      <button
                        className="text-button"
                        onClick={()=> onEdit(quote.id)}
                        type="button"
                      >
                        {view==='archived' ? 'View / Edit' : 'Open'}
                      </button>

                      {view==='archived' ? (
                        <>
                          <button
                            className="text-button"
                            disabled={actionLoading}
                            onClick={()=> handleRestore(quote)}
                            type="button"
                          >
                            Restore
                          </button>
                          <button
                            className="text-button"
                            disabled={actionLoading}
                            onClick={()=> openDialog(quote,'delete')}
                            type="button"
                          >
                            Delete
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="text-button"
                            disabled={actionLoading}
                            onClick={()=> openDialog(quote,'archive')}
                            type="button"
                          >
                            Archive
                          </button>
                          <button
                            className="text-button"
                            disabled={actionLoading}
                            onClick={()=> openDialog(quote,'delete')}
                            type="button"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {dialogQuote && (
        <div
          aria-labelledby="quote-action-title"
          aria-modal="true"
          role="dialog"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'grid',
            placeItems: 'center',
            padding: '20px',
            background: 'rgba(32,33,31,.42)'
          }}
        >
          <div
            style={{
              width: 'min(100%,420px)',
              padding: '24px',
              background: 'var(--paper)',
              border: '1px solid var(--line)',
              boxShadow: '0 18px 45px rgba(32,33,31,.18)'
            }}
          >
            <h2
              id="quote-action-title"
              style={{fontFamily: 'Manrope,sans-serif',fontSize: '20px'}}
            >
              {dialogType==='delete' ? 'Delete' : 'Archive'} {dialogQuote.quote_reference}?
            </h2>

            <p style={{marginTop: '10px',color: 'var(--muted)',fontSize: '13px'}}>
              {dialogType==='delete'
                ? 'This will permanently delete this quotation.'
                : 'This will remove the quotation from the active list but preserve all quote and acceptance data.'}
            </p>

            {actionError && (
              <p
                role="alert"
                style={{
                  marginTop: '12px',
                  color: 'var(--accent-dark)',
                  fontSize: '12px',
                  lineHeight: 1.45
                }}
              >
                {actionError}
              </p>
            )}

            <div style={{display: 'flex',justifyContent: 'flex-end',gap: '10px',marginTop: '24px'}}>
              <button
                className="button button--outline"
                disabled={actionLoading}
                onClick={closeDialogs}
                type="button"
              >
                Cancel
              </button>
              <button
                className="button button--accent"
                disabled={actionLoading}
                onClick={confirmAction}
                type="button"
              >
                {actionLoading
                  ? `${dialogType==='delete' ? 'Deleting' : 'Archiving'}…`
                  : dialogType==='delete' ? 'Delete Quote' : 'Archive Quote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {actionError && !dialogQuote && (
        <p
          role="alert"
          style={{marginTop: '12px',color: 'var(--accent-dark)',fontSize: '12px'}}
        >
          {actionError}
        </p>
      )}
    </>
  );
}

export default QuoteTable;